import React, { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { getCurrentAccount } from "./AccountApi";
import { useAxios } from "../../shared/technical/rest/AxiosProvider";
import { AxiosError } from "axios";
import { useUserManager } from "../../shared/technical/auth/UserManagerContext";
import { useTranslation } from "react-i18next";
import { User } from "./User";

const CurrentAccountContext = React.createContext<{
  account: User,
  refresh: () => void,
} | undefined>(undefined);

/**
 * Gets currently logged in user
 */
export function useAccount() {
  let accountContext = useContext(CurrentAccountContext);
  if (!accountContext) {
    throw new Error('No account context available! Probably not called inside CurrentAccountContext.Provider')
  }
  return accountContext.account;
}

/**
 * Let you refresh the currently logged in user
 */
export function useAccountRefresh() {
  let accountContext = useContext(CurrentAccountContext);
  if (!accountContext) {
    throw new Error('No account context available! Probably not called inside CurrentAccountContext.Provider')
  }
  return accountContext.refresh;
}

export function AccountProvider(props: {
  children: ReactNode
}) {
  const axios = useAxios();
  const userManager = useUserManager();
  const { i18n } = useTranslation();
  const [ account, setAccount ] = useState<User | undefined>();

  useEffect(() => {
    if (!account) {
      return
    }
    // console.info("Setting language to ", account.locale);
    i18n.changeLanguage(account.locale).then();
  }, [ account, i18n ]);

  useEffect(() => {
    getCurrentAccount(axios).then(setAccount).catch((error: AxiosError) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.info("User is not allowed to access this application! Will signout now! ", error);
        userManager.removeUser().then(() => userManager.signoutRedirect({
          post_logout_redirect_uri: `${window.location.href}`
        }));
      } else {
        throw new Error(error.message);
      }
    })
  }, [ axios, userManager ]);

  const refresh = useCallback(() => {
    getCurrentAccount(axios).then(setAccount);
  }, [ axios ])

  if (!account) {
    return null;
  }

  return (
    <CurrentAccountContext.Provider value={{ account, refresh }}>
      {props.children}
    </CurrentAccountContext.Provider>
  );
}
